/**
 * _execShared — helpers shared across the 6 executive (IC) dashboards
 * + their 3 mobile variants.
 *
 * Port of atoms from /tmp/aura-design-v2/extracted/aura-web-app/project/aura-exec.jsx.
 * Stays TS-only (no JSX) so we can keep the file small — dashboards inline their
 * visual helpers the same way manager dashboards do.
 *
 * Rules (binding):
 * - No tenant hardcoding. Gate tenant-specific features via
 *   `tenant?.enabledModules?.includes('<module>')` using the `moduleOn()` helper.
 * - Re-export `safeGet`, `formatINRCompact`, `splitINRCompact`, `formatNum`
 *   from the manager dashboard shared file so executive widgets can import
 *   everything from a single place.
 */

import type { TenantConfig } from '@/stores/auth';
import {
  safeGet,
  formatINRCompact,
  splitINRCompact,
  formatNum,
} from '../roles/_dashboardShared';

// ─── Re-export manager helpers ────────────────────────────────────────────
export { safeGet, formatINRCompact, splitINRCompact, formatNum };

// ─── Tenant module gating (D-09 / feedback_no_tenant_hardcoding) ─────────
/**
 * Check whether a module is enabled for the current tenant.
 * `enabledModules = null` (platform admin) = ALL modules enabled.
 * Use this instead of `tenant?.slug === 'vedaagro'` etc.
 */
export function moduleOn(
  tenant: TenantConfig | null | undefined,
  moduleKey: string,
): boolean {
  if (!tenant) return false;
  if (tenant.enabledModules === null) return true; // platform admin
  return tenant.enabledModules.includes(moduleKey);
}

// ─── Date + shift helpers ────────────────────────────────────────────────
export function todayLabel(): string {
  return new Date().toLocaleDateString('en-IN', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export function formatTimeShort(iso: string | null | undefined): string {
  if (!iso) return '\u2014';
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false });
  } catch {
    return '\u2014';
  }
}

export function formatDateShort(iso: string | null | undefined): string {
  if (!iso) return '\u2014';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  } catch {
    return '\u2014';
  }
}

// ─── Credit-burn hook (lightweight local state) ──────────────────────────
/**
 * The shell exposes a real credit counter via `CreditBurn` in TopBar. For
 * the executive dashboards we want an on-demand callback that:
 *   1. Optimistically flashes an animation (caller does it)
 *   2. Deducts credits via /api/v1/credits/deduct (best-effort, ignores errors
 *      — the actual AI feature endpoint also deducts, this is just UX feedback)
 *
 * Returns a `burn(cost, featureKey?)` function to call from button onClick.
 */
export function makeCreditBurn() {
  return async (cost: number, featureKey = 'ui_action') => {
    try {
      const { api } = await import('@/utils/api');
      await api.post('/credits/deduct', { cost, feature: featureKey });
      // Apr 23, 2026 (Bug 7) — nudge TopBar to refetch balance.
      const { bumpCredits } = await import('@/utils/credits-bus');
      bumpCredits();
    } catch {
      // Non-fatal — UI stays optimistic.
    }
  };
}
